import ZrLogo from '@assets/svg/ZrLogo';
import { useAppRouter } from '@my-medication/shared-components/providers/AppRouterProvider';
import { LanguageChooser } from '@my-medication/shared-components/shared/LanguageChooser';
import { useAdminStore } from '@store/adminStore';
import { AdminPortalKeys, Namespace } from '@i18n/translations';
import { type FunctionComponent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AccountMenu } from '../AccountMenu';
import { HeaderLayout } from '../HeaderLayout';
import { FontSettings } from '../FontSettings';
import { type NavItem } from '../Navigation';
import { type AppShellProps } from './AppShell.types';

const AppShell: FunctionComponent<AppShellProps> = ({
  currentRoles,
  fontSize,
  onFontSizeChange,
  onLogout,
  logoutLabel,
  menuLabel,
  logoClaimLabel,
  centered,
  dataTestid,
  className,
}) => {
  const { t, i18n } = useTranslation(Namespace.AdminPortal);
  const { navigateTo, resolveRoute } = useAppRouter();
  const currentLocale = i18n.language;

  const navItems: NavItem[] = useMemo(
    () => [
      {
        linkName: 'Admin Welcome',
        label: t(AdminPortalKeys.Navigation_Welcome),
        roles: ['admin', 'customerservice', 'ftl', 'va-employee', 'medicadmin', 'it'],
      },
      {
        linkName: 'Pharmacies List',
        label: t(AdminPortalKeys.Navigation_Pharmacies),
        roles: ['admin'],
      },
      {
        linkName: 'Patient List',
        label: t(AdminPortalKeys.Navigation_Patients),
        roles: ['admin', 'customerservice'],
        routeParams: { page: '1' },
      },
      {
        linkName: 'Doctor List',
        label: t(AdminPortalKeys.Navigation_Doctors),
        roles: ['customerservice', 'admin', 'medicadmin'],
        routeParams: { page: '1' },
      },
      {
        linkName: 'Open Requests List',
        label: t(AdminPortalKeys.Navigation_OpenRequests),
        roles: ['admin', 'customerservice'],
      },
      {
        linkName: 'Failed Requests List',
        label: t(AdminPortalKeys.Navigation_FailedRequests),
        roles: ['admin'],
      },
      {
        linkName: 'Admin Promotions List',
        label: t(AdminPortalKeys.Navigation_Promotions),
        roles: ['admin', 'customerservice'],
      },
      {
        linkName: 'Admin PickPost',
        label: t(AdminPortalKeys.Navigation_PickPost),
        roles: ['admin', 'ftl'],
      },
      {
        linkName: 'Admin PickPost View',
        label: t(AdminPortalKeys.Navigation_PickPostView),
        roles: ['admin', 'ftl', 'customerservice', 'va-employee'],
      },
      {
        linkName: 'Admin Maintenance',
        label: t(AdminPortalKeys.Navigation_Maintenance),
        roles: ['admin', 'it'],
      },
      {
        linkName: 'Admin Profile',
        label: t(AdminPortalKeys.Navigation_Profile),
        roles: ['admin', 'customerservice', 'ftl', 'va-employee', 'medicadmin', 'it'],
      },
    ],
    [t],
  );
  const homeHref = resolveRoute({
    name: 'Admin Welcome',
    params: { locale: currentLocale },
  }).href;
  const isAuthenticated = useAdminStore((s) => s.user.authenticated);

  return (
    <HeaderLayout
      dataTestid={dataTestid}
      className={className}
      navItems={navItems}
      currentRoles={currentRoles}
      isAuthenticated={isAuthenticated}
      centered={centered}
      onLogout={onLogout}
      logoutLabel={logoutLabel}
      menuLabel={menuLabel}
      logoSlot={
        <a
          href={homeHref}
          className="flex items-center gap-2 text-secondary-900 no-underline"
          onClick={(e) => {
            e.preventDefault();
            navigateTo({
              name: 'Admin Welcome',
              params: { locale: currentLocale },
            });
          }}
        >
          <ZrLogo
            className="h-10 w-24 max-w-none align-middle"
            role="img"
            aria-label={t(AdminPortalKeys.Header_LogoAlt)}
          />
          <span className="mt-2 hidden text-body-2 text-secondary-900 uppercase s:inline">{logoClaimLabel}</span>
        </a>
      }
      accountSlot={
        <>
          <FontSettings value={fontSize} onValueChange={onFontSizeChange} className="hidden s:flex" />
          <span className="hidden h-8 max-w-px min-w-px flex-1 bg-secondary-400 s:block" />
          <LanguageChooser className="hidden s:flex" />
          {isAuthenticated && (
            <>
              <span className="h-8 max-w-px min-w-px flex-1 bg-secondary-400" />
              <AccountMenu dataTestid="admin-account-menu" />
            </>
          )}
        </>
      }
    />
  );
};

AppShell.displayName = 'AppShell';
export { AppShell };
